import React, { useRef, useState } from 'react'
import classNames from 'classnames'
import { useIsomorphicLayoutEffect } from '../hooks'

type AnimationType = 'fade' | 'pull-above' | 'pull-right' | 'pull-below' | 'pull-left'

const animationClasses = {
  fade: {
    in: 'a-fade-in',
    out: 'a-fade-out',
  },
  'pull-above': {
    in: 'a-pull-from-above',
    out: 'a-push-to-above',
  },
  'pull-right': {
    in: 'a-pull-from-right',
    out: 'a-push-to-right',
  },
  'pull-below': {
    in: 'a-pull-from-below',
    out: 'a-push-to-below',
  },
  'pull-left': {
    in: 'a-pull-from-left',
    out: 'a-push-to-left',
  },
} as {
  [key in AnimationType]: {
    in: string
    out: string
  }
}

type Props = {
  visible: boolean
  children: React.ReactNode
  animationType?: AnimationType
  hideOnly?: boolean
  onHidden?: () => void
}

export const Animated = ({
  visible,
  children,
  onHidden,
  animationType = 'fade',
  hideOnly = false,
}: Props) => {
  const [show, setShow] = useState<boolean>(visible)
  const ref = useRef<HTMLDivElement | null>(null)

  useIsomorphicLayoutEffect(() => {
    if (!visible && ref.current) {
      ref.current.style.visibility = 'hidden'
    }
  }, [])

  useIsomorphicLayoutEffect(() => {
    if (visible) {
      if (ref.current) {
        ref.current.style.visibility = 'visible'
      }
      setShow(true)
    }
  }, [visible])

  const onAnimationEnd = () => {
    if (show && !visible) {
      if (ref.current) {
        ref.current.style.visibility = 'hidden'
      }
      setShow(false)
      onHidden && onHidden()
    }
  }

  return (
    <div
      ref={ref}
      className={classNames({
        [animationClasses[animationType].in]: visible,
        [animationClasses[animationType].out]: !visible,
      })}
      onAnimationEnd={onAnimationEnd}>
      {(hideOnly || show) && children}
    </div>
  )
}
